import type {
  OrderConfirmationOrder as PanelOrder,
  OrderConfirmationItem as PanelOrderItem,
  OrderConfirmationPanelProps,
  OrderConfirmationStatus as PanelStatus,
} from '@engyalo/delivery-ui-components'
import type {
  IOrderDetail,
  IOrderDetailItem,
  OrderExternalRefStatus,
} from '../graphQL/queries/order/types'
import { EXTERNAL_REF_FAILURE_STATUSES } from './orderConfirmation'
import { getOrderPaymentPickLabel } from './orderPaymentPickLabel'
import type { OrderCreationStatus as SliceStatus } from '../redux/slices/orders/types'
import type { RootState } from '../redux/store'
import { isHidePricesEnabled } from './hidePrices'
import { toIsoDate } from './toIsoDate'

const optional = <T>(value: T | null | undefined): T | undefined =>
  value ?? undefined

/**
 * The order's `externalRefStatus`, but only when it names a refusal on a
 * terminal failure.
 *
 * Two conditions, because either alone lets the pending value through: the
 * flow must have failed, and the value must be one the client system uses to
 * *refuse*. See the prop's own note in `getOrderConfirmationPanelProps`.
 * @internal
 */
const readRefFailureStatus = (
  state: RootState,
): OrderExternalRefStatus | undefined => {
  if (state.ordersSlice.creationStatus !== 'failed') return undefined

  const refStatus = state.ordersSlice.created?.externalRefStatus
  return refStatus && EXTERNAL_REF_FAILURE_STATUSES.includes(refStatus)
    ? refStatus
    : undefined
}

/**
 * `PanelOrder` with every member required, so omitting one is a build error.
 *
 * An object literal missing an *optional* property compiles silently, so adding
 * a field to the library's order would leave this mapper quietly dropping it —
 * and a dropped field here means the panel renders a placeholder forever while
 * the real value sits in the slice. Annotating the literal turns the next
 * version bump into a compile error that names the field.
 *
 * Deliberate omissions are then written as `field: undefined`, which is the
 * documentation worth having.
 */
type CompletePanelOrder = {
  // Mapped over `keyof Required<…>` rather than written with `-?`: that
  // modifier drops `undefined` from the value type as well as the optional
  // flag, which would forbid the deliberate `field: undefined` this exists to
  // encourage.
  //
  // The mapping is homomorphic — TypeScript copies modifiers from a mapped
  // type's modifiers type, which for a `keyof X` constraint is `X` — and that
  // is precisely why it works: the modifiers come from `Required<PanelOrder>`,
  // which has no optional flags left to copy, while `PanelOrder[K]` keeps its
  // `undefined`.
  //
  // **`Required<>` is the load-bearing token.** Rewriting this as
  // `[K in keyof PanelOrder]` silently disarms the guard — a later library
  // field goes missing with no error — and so does dropping the
  // `const mapped: CompletePanelOrder` annotation below and returning the
  // literal against the function's declared `PanelOrder | undefined`, which is
  // the likeliest "tidying" edit and looks harmless.
  //
  // A key remap (`as K`) is *not* one of the dangerous edits: with
  // `Required<>` still in the constraint the guard stays armed. An earlier
  // version of this comment said the opposite, which sent a reader to defend
  // the wrong token. `OrderGuardIsArmed` below now asserts the property
  // instead of trusting the prose.
  [K in keyof Required<PanelOrder>]: PanelOrder[K]
}

/**
 * The keys of `T` that are required, i.e. that a literal must actually supply.
 * @internal
 */
type RequiredKeys<T> = {
  [K in keyof T]-?: Record<string, never> extends Pick<T, K> ? never : K
}[keyof T]

type AssertTrue<T extends true> = T

/**
 * `CompletePanelOrder` is only useful while it requires *every* member — that
 * is the whole mechanism, and until now nothing checked it. Four one-token
 * edits disarm it silently, and the suite stays green through all of them
 * because a disarmed type simply stops complaining.
 *
 * This fails the type-check the moment any key of `PanelOrder` stops being
 * required by `CompletePanelOrder`, which is the guard's own arming condition
 * expressed as a compile-time assertion rather than as a comment asking people
 * to be careful. Same idiom as `EveryPanelStatusIsReachable` below.
 * @internal
 */
type OrderGuardIsArmed = AssertTrue<
  [keyof PanelOrder] extends [RequiredKeys<CompletePanelOrder>] ? true : false
>
/**
 * The write half's statuses, mapped onto the panel's.
 *
 * Typed both ways for the reason `PANEL_STATUS_BY_SLICE_STATUS` is in
 * `orderDetailPanel`: the key type is the slice's union, so adding or removing
 * a member there fails the build here rather than falling through silently,
 * and the value type is the library's, so a status the panel does not publish
 * cannot be sent to it.
 *
 * Two members do real work:
 *
 * - `confirming` becomes **`pending`**. The slice collapses "the mutation is
 *   in flight" and "the poll is running" into one member because the shopper
 *   is told the same thing in both; the panel names that state `pending`.
 * - `idle` becomes **`creating`**, and it is the one entry that is a judgement
 *   rather than a rename. `idle` means the flow has not started, so there is
 *   no order and nothing in flight — but the panel has no such member, and a
 *   total `Record` is what forces a decision here rather than letting the
 *   lookup return `undefined`. (The panel's `status = 'creating'` is a
 *   *destructuring* default: it fires for an absent status, not for an
 *   unrecognised one, which flows through verbatim to a bare spinner.) In
 *   practice
 *   the state is unreachable from the cart, which dispatches `setOrderCreating`
 *   before `createOrder` goes out. It *was* reachable by opening the
 *   confirmation route directly, and there the shopper saw the configured
 *   "placing your order" copy over no order, indefinitely, since `creating` is
 *   in-flight and the panel draws a spinner for it — with no exit, because the
 *   panel's own one is gated on a `returnToConversationLabel` that no
 *   `defaultProps` forwards, and `data-exit` cannot flag it either since the
 *   marker is suppressed in flight. **The mapping is still this one**: every
 *   alternative is worse, `unknown` and `created` both asserting an order
 *   exists. What changed is that the case no longer reaches the panel *through
 *   an `orderConfirmation`-typed container* —
 *   `templates/OrderConfirmation.tsx` redirects out when
 *   `creationStatus === 'idle' && !created` (COM-819), which is the route
 *   guard this note asked for. **Read the qualifier as a limit on the fix**:
 *   this injector runs off a component's `importRoute`
 *   (`useCinnamonProps`' `PANEL_PROP_INJECTORS`) and never consults
 *   `container.type`, which is optional, so a `default`-type container holding
 *   an `OrderConfirmationPanel` reaches this map with `idle` exactly as before
 *   and draws the same spinner over no order. `idle` still maps to `creating`
 *   here, and must: the guard keys on the absence of an *attempt*, so an
 *   `idle` status alongside a created order still renders through this map.
 * @internal
 */
const PANEL_STATUS_BY_SLICE_STATUS = {
  idle: 'creating',
  creating: 'creating',
  confirming: 'pending',
  created: 'created',
  confirmed: 'confirmed',
  failed: 'failed',
  unknown: 'unknown',
} as const satisfies Record<SliceStatus, PanelStatus>

/**
 * Fails if any panel status is unreachable from the map above.
 *
 * The domain half is guarded by `satisfies` — every slice status must have an
 * entry, and none may name a status the panel does not publish. This is the
 * other direction, and it needs `as const` to work at all: under a
 * `Record<SliceStatus, PanelStatus>` *annotation* the values widen to
 * `PanelStatus`, so an inverse assertion would be vacuous and compile even for
 * a map with an unreachable status.
 *
 * What it buys: if the library grows a status this flow never produces, that
 * is either a state left unwired or one belonging to another screen — and
 * either way a tenant could author `messages` for copy no shopper can reach.
 * @internal
 */
type AssertNever<T extends never> = T
type EveryPanelStatusIsReachable = AssertNever<
  Exclude<PanelStatus, (typeof PANEL_STATUS_BY_SLICE_STATUS)[SliceStatus]>
>

/**
 * One order line in the panel's shape.
 *
 * Deliberately not `orderDetailPanel`'s `toPanelItem`, though the arithmetic
 * is the same: that one returns an `OrderDetailItem`, which declares
 * `imageURL`, and `OrderConfirmationItem` does not. Passing the wider object
 * would be an excess property the panel ignores — harmless on screen, and a
 * type error at the boundary that exists to stop exactly that.
 *
 * `priceTotal` is derived here rather than read, because the API sends no line
 * total. `price` is `number | null` on the schema and fails in two directions:
 * `null * quantity` is **`0`**, and an *absent* key gives `NaN`. Neither
 * reaches `Price` — the panel's own `isRenderableAmount` requires a finite
 * non-zero amount — so an unguarded line renders **with no amount at all**,
 * which reads as free rather than as an error. (`$NaN` is what `Price` would
 * print if it were reached; the panel's gate is what stops it.)
 *
 * `typeof price === 'number'` is unkillable at runtime — `Number.isFinite`
 * coerces nothing, so it already rejects every non-number — and exists to
 * narrow for `tsc`. `quantity` is non-null on the schema and guarded anyway.
 * @internal
 */
const toPanelItem = (item: IOrderDetailItem): PanelOrderItem => {
  const { sku, quantity, name, price, customFields } = item
  const hasLineTotal =
    typeof price === 'number' &&
    Number.isFinite(price) &&
    Number.isFinite(quantity)

  return {
    sku,
    quantity,
    name: optional(name),
    price: optional(price),
    priceTotal: hasLineTotal ? price * quantity : undefined,
    customFields: optional(customFields),
  }
}

/**
 * The created order in the confirmation panel's shape.
 *
 * Narrower than `toOrderDetailPanelOrder` because the panel is: no customer
 * address, no fee breakdown, no discounts. This screen answers "did this just
 * work", and a field the panel does not render but does receive is a money
 * value nobody is guarding.
 *
 * `null` in, `undefined` out — the panel's `order` prop is optional and the
 * slice's field is nullable, and `creating` is a real state with no order.
 *
 * `expectedDeliveryDate` and `paymentMethod` are forwarded as of
 * `@engyalo/delivery-ui-components@1.20.0`, which is the release that added
 * the two rows they feed. They were deliberately absent while the pinned
 * version was `1.19.1`, and `CompletePanelOrder` is what turned the bump into
 * a compile error naming both rather than a silently missing row — it did
 * exactly that on the upgrade.
 *
 * Both are raw backend values, and the panel is what maps them: the status
 * enum through `statusMap`, the payment enum through `paymentMethodLabels`,
 * each authored in config. Cinnamon deliberately does not translate either
 * here — a label is a tenant's word for a thing, and this file adds no layout
 * or copy decision.
 */
export const toOrderConfirmationOrder = (
  order?: IOrderDetail | null,
): PanelOrder | undefined => {
  if (!order) return undefined

  const mapped: CompletePanelOrder = {
    id: order.id,
    sequence: optional(order.sequence),
    status: optional(order.status),
    // Both dates arrive as the `Date` scalar's `{ timestamp, string }` object,
    // so they are normalised rather than copied — the same treatment
    // `toOrderDetailPanelOrder` gives them. `optional` alone would hand the
    // object to the panel, which renders it as no date at all (COM-823). This
    // file was written against the pre-COM-823 types and did not exist on
    // `main` when that fix widened them, so it is the one date consumer the
    // fix could not reach.
    createdAt: toIsoDate(order.createdAt),
    expectedDeliveryDate: toIsoDate(order.expectedDeliveryDate),
    paymentMethod: optional(order.paymentMethod),
    items: Array.isArray(order.items)
      ? order.items.filter(Boolean).map(toPanelItem)
      : undefined,
    total: optional(order.total),
    customFields: optional(order.customFields),
  }

  return mapped
}

/**
 * The `OrderConfirmationPanel` props cinnamon owns rather than the config
 * author.
 *
 * Injected in `useCinnamonProps` for any component whose `importRoute` is
 * `OrderConfirmationPanel`, exactly as `getOrderDetailPanelProps` is for the
 * detail screen. None of these is authorable as a `{{variable}}`: four are
 * derived from state the config DSL cannot compute and one — `order` — is a
 * shape it cannot build.
 *
 * Everything else stays in config — every label, the state copy, the currency,
 * the status map, `fieldsToDisplay`, the slots and the return-to-conversation
 * action. This file adds no layout decision to cinnamon.
 *
 * Injected **before** `processData` and `maskPricesForDisplay` run, so the
 * order is masked with everything else. Injecting afterwards would hand a
 * hide-prices shopper the real totals.
 *
 * `declineReason` is passed in every state, not only `failed`, and the panel's
 * own `status === 'failed'` gate is what makes that safe. A second gate here
 * would be two places to keep in step.
 *
 * It does **not** rest on the slice clearing `creationError`:
 * `setOrderCreationPollResult` and `setOrderOutcomeUnknown` both change the
 * status without touching the field. What holds instead is reachability — `setOrderCreationFailed` is terminal at
 * every call site, and `setOrderCreating` clears the field as it arms the next
 * attempt — so a non-`failed` status cannot arrive with a reason set. Flow
 * discipline, not a reducer property, which is worth knowing before adding a
 * reducer.
 */
/**
 * The six props this file owns, checked against the panel that receives them.
 *
 * `CompletePanelOrder` guards the *order's* field names; this guards the prop
 * names, which were otherwise unchecked — the injector's return is widened to
 * `UnresolvedRecord` by the `Map` it is registered in, so a library rename of
 * `declineReason` or `hidePrices` would compile, land unread on the panel, and
 * silently stop suppressing a decline reason or money. Same drift, one level
 * up.
 *
 * **It guards names and value types, not presence.** `Pick` preserves the
 * optional modifier and every panel prop is optional, so `{}` satisfies this
 * type — dropping a line from the returned object is a compile *success*. That
 * case is covered by assertions instead, which is why each forwarded prop has
 * a test naming it. `CompletePanelOrder` is the one that makes omission fatal,
 * and it can do that only because `Required<>` strips the modifiers first.
 */
type InjectedConfirmationProps = Pick<
  OrderConfirmationPanelProps,
  | 'order'
  | 'status'
  | 'externalRefStatus'
  | 'hidePrices'
  | 'declineReason'
  | 'paymentMethodLabel'
  | 'timeZone'
>

export const getOrderConfirmationPanelProps = (
  state: RootState,
): InjectedConfirmationProps => ({
  order: toOrderConfirmationOrder(state.ordersSlice.created),
  /**
   * The picked payment option's label, when the shopper's `PAYMENT_METHODS`
   * pick carried one. See `getOrderPaymentPickLabel` for the extraction. The
   * library's precedence is a non-empty string wins over
   * `paymentMethodLabels[method]` (COM-1252), so `undefined` leaves today's
   * map lookup in charge — the "orders without a keyed pick show today's
   * label" acceptance criterion for COM-1259.
   *
   * Read off `ordersSlice.created` rather than off the Redux
   * `checkoutSlice.available` the cart's `paymentMethodLabel` reads. The
   * order's own `checkoutRules` are the only source available to a shopper
   * who opens an older confirmation across the session boundary; the cart's
   * reader was deliberately given the shorter path because the pick is in
   * `available` whenever the cart is on screen.
   */
  paymentMethodLabel: getOrderPaymentPickLabel(state.ordersSlice.created),
  status: PANEL_STATUS_BY_SLICE_STATUS[state.ordersSlice.creationStatus],
  /**
   * The tenant's zone, so an order's timestamp is not read in the viewer's.
   *
   * `configuration.i18n.timezone`, absent for most storefronts. `optional()`
   * because the field is nullable server-side *and* selected, so an authored-
   * but-empty value arrives as `null` rather than as a missing key — and the
   * panel's prop is `string | undefined`. Every other forwarded wire value in
   * these adapters goes through the same helper; this one is not an exception.
   *
   * No default: cinnamon does not know a tenant's zone, and the panel already
   * falls back to the runtime default, so an unset field behaves exactly as it
   * did before the prop existed.
   *
   * This does not reach the delivery date. That field names a calendar day and
   * the library renders it as stored, in UTC — passing a zone there would move
   * it to the previous day for any tenant west of UTC, which is COM-816
   * itself rather than its fix.
   */
  timeZone: optional(
    state.defaultSlice.sessionData?.configuration?.i18n?.timezone,
  ),
  /**
   * Forwarded only on `failed`, though the panel accepts it in any state.
   *
   * It is documented there as a copy *selector* — the only way to tell a
   * client-system decline (`REJECTED`) from a transport failure (`ERROR`) —
   * and the panel prefers `externalRefMessages[externalRefStatus]` whenever
   * that map has an entry for the value — with **no status gate of its own**.
   * So a tenant who authored `externalRefMessages.CONFIRMED` could show
   * "confirmed" over an order still being confirmed: a success the screen
   * never observed. `setOrderConfirming` and `setOrderCreationPollResult` both
   * write `created` from a tick carrying whatever the server sent, so that is
   * reachable rather than theoretical.
   *
   * **Gated on the value as well as the status**, and the status alone was not
   * enough. `PROCESSING` is the pending value — the CREATE_ORDER add-on writes
   * it unconditionally and it survives later merges — and it stays on `created`
   * through a decline, because the confirm-decline path dispatches
   * `setOrderCreationFailed({ message })` with no order and the reducer
   * replaces `created` only when the payload carries one. So `failed` alone
   * forwarded `PROCESSING`, and a tenant authoring the very map this prop
   * exists for read in-flight copy over a terminal decline.
   *
   * `EXTERNAL_REF_FAILURE_STATUSES` is the right filter rather than a
   * hand-written list: it is the closed, deliberately non-configurable set of
   * values that mean the client system refused the order, which is exactly the
   * vocabulary the panel documents this prop for.
   */
  externalRefStatus: readRefFailureStatus(state),
  /**
   * Slot text is a string, and `maskPricesForDisplay` is an allowlist of prop
   * names over numbers, so it cannot see a price inside `'Precio por kilo
   * $98.90'`. It cannot see one inside a server-authored decline reason
   * either, which is why the panel suppresses that outright under this flag.
   */
  hidePrices: isHidePricesEnabled(state),
  declineReason: optional(state.ordersSlice.creationError),
})

export default getOrderConfirmationPanelProps
